/**
 * Plan 25 — `/pair?token=...` landing page.
 *
 * The tablet (a freshly-paired kiosk) lands here after scanning the
 * QR code (or pasting the URL). The page:
 *
 *   1. Reads the `?token=...` query parameter — this is the plaintext
 *      bearer the SCHOOL_ADMIN's pair-sheet just generated.
 *   2. Persists the token in `localStorage.kioskToken` so the http.ts
 *      apiFetch helper sends it as `Authorization: Kiosk <token>` on
 *      every subsequent REST call.
 *   3. Force-reloads the profile store so the bootstrap response
 *      sees the paired session (kioskContext.paired === true), then
 *      redirects to `/cafeteria`.
 *
 * If the token query is missing, the page shows a friendly error
 * and a "Back to sign-in" link. The page sits OUTSIDE the auth-gated
 * shell so a tablet without a multipass session can still complete
 * the landing — the kiosk bearer IS the credential.
 */

import { Alert, AlertDescription, AlertTitle } from '@ordo/ui/alert'
import { Button } from '@ordo/ui/button'
import { Skeleton } from '@ordo/ui/skeleton'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'

import { KIOSK_TOKEN_STORAGE_KEY } from '@/api/http'
import { getOrCreateBrowserIdentifier } from '@/api/kiosk-sessions'
import { useProfileStore } from '@/stores/profile'

type PairStatus = 'pending' | 'redirecting' | 'missing-token' | 'persistence-failed'

export function PairPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const refresh = useProfileStore((s) => s.refresh)
  const [status, setStatus] = useState<PairStatus>('pending')

  useEffect(() => {
    const token = params.get('token')
    if (!token || token.length === 0) {
      setStatus('missing-token')
      return
    }
    try {
      localStorage.setItem(KIOSK_TOKEN_STORAGE_KEY, token)
      // Reserve a stable browser identifier — the apiFetch helper
      // forwards this as X-Kiosk-Browser-Id so the server's drift
      // rule has a stable baseline.
      getOrCreateBrowserIdentifier()
    } catch {
      // localStorage disabled / private mode — the kiosk can't run
      // without persistence; surface the error and let the admin
      // re-pair via the cafeteria URL on a different browser.
      setStatus('persistence-failed')
      return
    }
    setStatus('redirecting')
    // Eagerly refresh the profile store so the CafeteriaLauncher
    // visibility check (kioskContext.paired === true) is satisfied
    // before we navigate into `/cafeteria`. Failures are non-fatal —
    // the cafeteria page itself will bootstrap on first render.
    void refresh().finally(() => {
      navigate('/cafeteria', { replace: true })
    })
    // We intentionally re-run only on a query-param change; the
    // navigate / refresh closures are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params])

  if (status === 'missing-token') {
    return (
      <div className="flex h-screen items-center justify-center p-6">
        <Alert variant="error" className="max-w-md">
          <AlertTitle>Pairing URL is missing the token</AlertTitle>
          <AlertDescription>
            Ask the school admin to generate a new pairing link from <code>Settings → My School → Devices</code>.
          </AlertDescription>
          <div className="mt-3">
            <Button asChild size="sm" variant="outline">
              <Link to="/signin">Back to sign-in</Link>
            </Button>
          </div>
        </Alert>
      </div>
    )
  }

  if (status === 'persistence-failed') {
    return (
      <div className="flex h-screen items-center justify-center p-6">
        <Alert variant="error" className="max-w-md">
          <AlertTitle>Couldn't save the pairing token</AlertTitle>
          <AlertDescription>
            This browser isn't allowing local storage. Open the pairing link in a different browser, or ask IT to enable
            cookies + storage on this tablet.
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4">
      <Skeleton className="h-12 w-48" />
      <p className="text-sm text-muted-foreground">Pairing this tablet…</p>
    </div>
  )
}
